import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { useTimelineItemResize } from 'hooks/timeline'
import {
  convertAudioItemsToTimelineItems,
  convertTimelineItemsToAudioItems
} from 'utils/timeline'
import { clampFadeEffectsToDuration } from 'utils/audio'
import { IAudioItem } from 'types/audio'

export const useAudioItemResize = ({
  itemId,
  lineIndex,
  audioItems,
  updateAudioItems
}: {
  itemId: string
  lineIndex: number
  audioItems: IAudioItem[]
  updateAudioItems: (audioItem: IAudioItem[]) => void
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const audioItemsRef = useRef<IAudioItem[]>(audioItems)

  const timelineItemsInSameLine = useMemo(() => {
    const audioItemsInSameLine = audioItems.filter(
      item => item.lineIndex === lineIndex
    )
    return convertAudioItemsToTimelineItems(audioItemsInSameLine)
  }, [audioItems, lineIndex])

  const {
    items: timelineItems,
    handleLeftResize,
    handleRightResize
  } = useTimelineItemResize(timelineItemsInSameLine, isResizing)

  // update audio items if timeline items changed due to resize
  useEffect(() => {
    updateAudioItems(
      convertTimelineItemsToAudioItems(timelineItems, audioItems)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineItems])

  audioItemsRef.current = useMemo(() => {
    return convertTimelineItemsToAudioItems(timelineItems, audioItems)
  }, [timelineItems, audioItems])

  const onResizeEnd = useCallback(() => {
    setIsResizing(false)
    dispatch(editorThunks.setIsTimelineItemResizing(false))
    // Trimming changes duration → a fade may now exceed half the clip; clamp it.
    dispatch(
      projectDataThunks.setMultilineAudios(
        audioItemsRef.current.map(item => ({
          ...item,
          audioEffects: clampFadeEffectsToDuration(
            item.audioEffects,
            item.duration
          )
        }))
      )
    )
  }, [])

  const onResizeStart = useCallback(() => {
    setIsResizing(true)
    dispatch(editorThunks.setIsTimelineItemResizing(true))
  }, [])

  const _handleLeftResize = useCallback(
    (newWidth: number) => {
      handleLeftResize(itemId, newWidth)
    },
    [handleLeftResize, itemId]
  )

  const _handleRightResize = useCallback(
    (newWidth: number) => {
      handleRightResize(itemId, newWidth)
    },
    [handleRightResize, itemId]
  )
  return {
    onResizeStart,
    onResizeEnd,
    handleLeftResize: _handleLeftResize,
    handleRightResize: _handleRightResize
  }
}
